import { useRef, useCallback } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Link, Heading1, Heading2, Heading3, Quote, Undo, Redo, Type } from 'lucide-react';
import { Button } from '../ui/button';

function ToolbarButton({ icon: Icon, command, value, title }) {
  const exec = useCallback((e) => {
    e.preventDefault();
    document.execCommand(command, false, value || null);
  }, [command, value]);
  return (
    <Button type="button" variant="ghost" size="sm" onMouseDown={exec} title={title} className="h-8 w-8 p-0 text-zinc-500 hover:text-[#7551e8] hover:bg-[#F4F0FF]">
      <Icon size={15} />
    </Button>
  );
}

export function RichTextEditor({ value, onChange, className = "" }) {
  const editorRef = useRef(null);

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/html') || e.clipboardData.getData('text/plain');
    document.execCommand('insertHTML', false, text);
  }, []);

  const insertLink = useCallback((e) => {
    e.preventDefault();
    const url = prompt('Enter URL:');
    if (url) document.execCommand('createLink', false, url);
  }, []);

  const formatBlock = useCallback((tag) => (e) => {
    e.preventDefault();
    document.execCommand('formatBlock', false, tag);
  }, []);

  return (
    <div className={`border border-zinc-200 rounded-lg overflow-hidden bg-white ${className}`} data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-zinc-200 bg-zinc-50">
        <ToolbarButton icon={Bold} command="bold" title="Bold" />
        <ToolbarButton icon={Italic} command="italic" title="Italic" />
        <ToolbarButton icon={Underline} command="underline" title="Underline" />
        <div className="w-px h-5 bg-zinc-300 mx-1" />
        <Button type="button" variant="ghost" size="sm" onMouseDown={formatBlock('h2')} title="Heading 2" className="h-8 w-8 p-0 text-zinc-500 hover:text-[#7551e8] hover:bg-[#F4F0FF]"><Heading2 size={15} /></Button>
        <Button type="button" variant="ghost" size="sm" onMouseDown={formatBlock('h3')} title="Heading 3" className="h-8 w-8 p-0 text-zinc-500 hover:text-[#7551e8] hover:bg-[#F4F0FF]"><Heading3 size={15} /></Button>
        <Button type="button" variant="ghost" size="sm" onMouseDown={formatBlock('p')} title="Paragraph" className="h-8 w-8 p-0 text-zinc-500 hover:text-[#7551e8] hover:bg-[#F4F0FF]"><Type size={15} /></Button>
        <div className="w-px h-5 bg-zinc-300 mx-1" />
        <ToolbarButton icon={List} command="insertUnorderedList" title="Bullet List" />
        <ToolbarButton icon={ListOrdered} command="insertOrderedList" title="Numbered List" />
        <ToolbarButton icon={Quote} command="formatBlock" value="blockquote" title="Blockquote" />
        <div className="w-px h-5 bg-zinc-300 mx-1" />
        <Button type="button" variant="ghost" size="sm" onMouseDown={insertLink} title="Insert Link" className="h-8 w-8 p-0 text-zinc-500 hover:text-[#7551e8] hover:bg-[#F4F0FF]"><Link size={15} /></Button>
        <div className="w-px h-5 bg-zinc-300 mx-1" />
        <ToolbarButton icon={Undo} command="undo" title="Undo" />
        <ToolbarButton icon={Redo} command="redo" title="Redo" />
      </div>
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        dangerouslySetInnerHTML={{ __html: value }}
        className="min-h-[280px] p-4 prose prose-sm max-w-none focus:outline-none prose-headings:font-['Outfit'] prose-headings:text-[#0A0A0A] prose-a:text-[#7551e8]"
        data-testid="rich-text-content"
      />
    </div>
  );
}
